/**
 * SubscriptionManager Component
 * 
 * Gerenciamento de assinatura do usuário
 * - Exibe plano atual e status
 * - Próxima cobrança e valor
 * - Histórico de transações
 * - Ações: alterar plano, cancelar
 * 
 * Fase 5 - UI de Pagamentos
 */

import { useState, useEffect } from 'react';
import { Crown, Zap, Star, Calendar, CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { PaymentService } from '../../services/PaymentService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function SubscriptionManager() {
  const { user } = useAuth();
  const { subscription, loading: subLoading, refresh } = useSubscription();
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Buscar histórico de transações
  useEffect(() => {
    async function fetchTransactions() {
      if (!user?.id) return;

      try {
        setLoadingTransactions(true);
        const data = await PaymentService.getTransactionHistory(user.id);
        setTransactions(data || []);
      } catch (error) {
        console.error('Erro ao buscar transações:', error);
      } finally {
        setLoadingTransactions(false);
      }
    }

    fetchTransactions();
  }, [user?.id]); // Apenas quando o ID do usuário mudar

  // Forçar um refresh da assinatura ao montar a aba de Pagamento
  useEffect(() => {
    try {
      console.debug('[SubscriptionManager] Forçando refresh() no mount da aba Pagamento');
      void refresh();
    } catch {
      // noop
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Configuração de ícones por plano
  const planIcons = {
    free: { icon: Star, color: 'gray', label: 'Gratuito' },
    basic: { icon: Zap, color: 'blue', label: 'Básico' },
    premium: { icon: Crown, color: 'purple', label: 'Premium' }
  } as const;

  // Status labels
  const statusLabels = {
    active: { label: 'Ativo', color: 'green', icon: '✅' },
    pending: { label: 'Aguardando Confirmação', color: 'blue', icon: '⏳' },
    processing: { label: 'Processando Pagamento', color: 'blue', icon: '⏳' },
    past_due: { label: 'Pagamento Pendente', color: 'yellow', icon: '⚠️' },
    canceled: { label: 'Cancelado', color: 'red', icon: '❌' },
    grace_period: { label: 'Período de Carência', color: 'orange', icon: '⏳' },
    pending_downgrade: { label: 'Downgrade agendado', color: 'blue', icon: 'ℹ️' }
  } as const;

  if (subLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Você não tem uma assinatura ativa.</p>
        <button
          onClick={() => navigate('/payment')}
          className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700"
        >
          Escolher Plano
        </button>
      </div>
    );
  }

  const planConfig = planIcons[subscription.plan_tier as keyof typeof planIcons] || { icon: Star, color: 'gray', label: String(subscription.plan_tier || 'Plano') };
  const statusConfig = (statusLabels as any)[subscription.status] || { label: String(subscription.status || 'Indefinido'), color: 'gray', icon: 'ℹ️' };
  // Derivar rótulo quando status = pending mas já temos desfecho na última transação
  const lastTx = transactions[0];
  let displayStatusConfig = statusConfig;
  if (subscription.status === 'pending' && lastTx) {
    if (lastTx.status === 'failed') {
      displayStatusConfig = { label: 'Pagamento Falhou', color: 'red', icon: '❌' } as any;
    } else if (lastTx.status === 'refunded') {
      displayStatusConfig = { label: 'Pagamento Estornado', color: 'gray', icon: '↩️' } as any;
    }
  }
  const PlanIcon = (planConfig as any).icon || Star;
  // Valor a exibir: prioriza subscription.amount_cents (se existir), senão usa último pagamento
  const amountCentsToDisplay: number | null =
    (typeof (subscription as any)?.amount_cents === 'number'
      ? (subscription as any).amount_cents
      : (typeof lastTx?.amount === 'number' ? lastTx.amount : null));

  return (
    <div className="space-y-6">
      {/* Plano Atual */}
      <div>
        <h2 className="text-2xl font-bold mb-4">Plano Atual</h2>
        
        <div className={`
          p-6 rounded-2xl
          ${subscription.plan_tier === 'premium' ? 'bg-gradient-to-br from-purple-50 to-pink-50' : 'bg-blue-50'}
        `}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <PlanIcon className={`w-8 h-8 text-${planConfig.color}-600`} />
              <div>
                <h3 className="text-xl font-bold capitalize">{planConfig.label}</h3>
                <p className="text-sm text-gray-600">
                  {displayStatusConfig.icon} {displayStatusConfig.label}
                </p>
                {subscription.status === 'pending_downgrade' && subscription.pending_downgrade_at && (
                  <div className="mt-1">
                    <p className="text-xs text-gray-500">
                      Aplicação prevista para {format(new Date(subscription.pending_downgrade_at), "dd/MM/yyyy", { locale: ptBR })}
                    </p>
                    <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-2">
                      <p className="text-xs text-gray-600">
                        Você permanecerá com acesso ao plano atual até {format(new Date(subscription.pending_downgrade_at), "dd/MM/yyyy", { locale: ptBR })}.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => navigate('/payment')}
              className="text-sm text-blue-600 hover:underline font-medium"
            >
              Alterar Plano
            </button>
          </div>

          {/* Info de Renovação */}
          {subscription.plan_tier !== 'free' && subscription.status === 'active' && (
            <div className="bg-white/70 p-4 rounded-xl">
              <p className="text-sm font-semibold mb-3">Renovação Automática</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 mb-1">Próxima cobrança</p>
                  <p className="font-semibold">
                    {subscription.active_until 
                      ? format(new Date(subscription.active_until), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                      : 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-gray-600 mb-1">Valor</p>
                  <p className="font-semibold">
                    {amountCentsToDisplay != null ? `R$ ${(amountCentsToDisplay / 100).toFixed(2)}` : '—'}
                  </p>
                </div>

                <div>
                  <p className="text-gray-600 mb-1">Ciclo</p>
                  <p className="font-semibold capitalize">
                    {subscription.billing_cycle === 'monthly' && 'Mensal'}
                    {subscription.billing_cycle === 'semester' && 'Semestral'}
                    {subscription.billing_cycle === 'annual' && 'Anual'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Alertas */}
          {subscription.status === 'past_due' && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-800">Pagamento Pendente</p>
                <p className="text-sm text-yellow-700 mt-1">
                  Houve um problema com seu último pagamento. Atualize seus dados de pagamento para manter o acesso.
                </p>
                <button
                  onClick={() => navigate('/payment')}
                  className="mt-2 text-sm text-yellow-800 underline font-medium"
                >
                  Atualizar Pagamento
                </button>
              </div>
            </div>
          )}

          {subscription.status === 'grace_period' && (
            <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-orange-800">Período de Carência</p>
                <p className="text-sm text-orange-700 mt-1">
                  Sua assinatura expirará em breve. Renove agora para manter todos os benefícios.
                </p>
                <button
                  onClick={() => navigate('/payment')}
                  className="mt-2 text-sm text-orange-800 underline font-medium"
                >
                  Renovar Agora
                </button>
              </div>
            </div>
          )}

          {subscription.status === 'canceled' && (
            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-800">Assinatura Cancelada</p>
                <p className="text-sm text-gray-600 mt-1">
                  Você ainda tem acesso até {subscription.active_until ? format(new Date(subscription.active_until), "dd/MM/yyyy", { locale: ptBR }) : 'o fim do período'}. Após essa data, seu plano voltará para Gratuito.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Histórico de Pagamentos */}
      <div>
        <h2 className="text-xl font-bold mb-4">Histórico de Pagamentos</h2>

        {loadingTransactions ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600">Nenhuma transação ainda</p>
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map((transaction) => (
              <TransactionRow key={transaction.id} transaction={transaction} />
            ))}
          </div>
        )}
      </div>

      {/* Ações */}
      {subscription.plan_tier !== 'free' && subscription.status !== 'canceled' && (
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate('/payment')}
            className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all"
          >
            Alterar Plano
          </button>

          <button
            onClick={() => setShowCancelModal(true)}
            className="flex-1 py-3 bg-red-50 text-red-600 border border-red-200 rounded-xl font-semibold hover:bg-red-100 transition-all"
          >
            Cancelar Assinatura
          </button>
        </div>
      )}

      {/* Modal de Cancelamento */}
      {showCancelModal && (
        <CancelSubscriptionModal
          subscriptionId={subscription.id}
          onClose={() => setShowCancelModal(false)}
          onSuccess={() => {
            setShowCancelModal(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// Componente de linha de transação
function TransactionRow({ transaction }: { transaction: any }) {
  const statusColors = {
    paid: 'text-green-600 bg-green-50',
    pending: 'text-yellow-600 bg-yellow-50',
    failed: 'text-red-600 bg-red-50',
    refunded: 'text-gray-600 bg-gray-50'
  };

  const statusLabels = {
    paid: 'Pago',
    pending: 'Pendente',
    failed: 'Falhou',
    refunded: 'Estornado'
  };

  return (
    <div className="bg-white border rounded-lg p-4 flex items-center justify-between hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4">
        <CreditCard className="w-5 h-5 text-gray-400" />
        <div>
          <p className="font-medium">
            {transaction.transaction_type === 'subscription' && 'Assinatura'}
            {transaction.transaction_type === 'upgrade' && 'Upgrade de Plano'}
            {transaction.transaction_type === 'sos_credit' && 'Crédito SOS'}
          </p>
          <p className="text-sm text-gray-600">
            {format(new Date(transaction.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <p className="font-semibold">
          R$ {(transaction.amount / 100).toFixed(2)}
        </p>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[transaction.status as keyof typeof statusColors]}`}>
          {statusLabels[transaction.status as keyof typeof statusLabels]}
        </span>
      </div>
    </div>
  );
}

// Modal de cancelamento
function CancelSubscriptionModal({ 
  subscriptionId, 
  onClose, 
  onSuccess 
}: { 
  subscriptionId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleCancel() {
    try {
      setLoading(true);
      setError(null);

      await PaymentService.cancelSubscription(subscriptionId);

      // Mostrar mensagem de sucesso
      setSuccess(true);
      
      // Aguardar 2 segundos e fechar (diminuído para melhorar UX)
      setTimeout(() => {
        onSuccess();
        // Forçar refresh da página após fechar o modal para garantir atualização
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      // Se já estava cancelado, tratar como sucesso
      if (err.message?.includes('já foi cancelada') || err.message?.includes('already_canceled')) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          window.location.reload();
        }, 2000);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full">
        {success ? (
          // Tela de sucesso
          <>
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✅</span>
              </div>
              <h3 className="text-xl font-bold mb-2">Assinatura Cancelada</h3>
              <p className="text-gray-600">
                Sua assinatura foi cancelada com sucesso. Você manterá acesso até o fim do período já pago.
              </p>
            </div>
          </>
        ) : (
          // Tela de confirmação
          <>
            <h3 className="text-xl font-bold mb-4">Cancelar Assinatura</h3>

            <p className="text-gray-600 mb-6">
              Tem certeza que deseja cancelar sua assinatura? Você perderá acesso aos benefícios.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all disabled:opacity-50"
              >
                Voltar
              </button>

              <button
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Cancelando...
                  </>
                ) : (
                  'Confirmar Cancelamento'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
